import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import AdminListView from '@/components/AdminListView';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  Search, Users, UserCheck, Calendar, CheckCircle, XCircle, Eye, EyeOff, Mail, Phone, MapPin,
  ChevronDown, ChevronUp, FileText, CreditCard, Clock, DollarSign, Banknote, Shield, Plus, X,
  ArrowLeft, Save, Upload, Globe, Stethoscope, Edit, Loader2, Download, IdCard, ExternalLink,
  Trash2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ROLE_OPTIONS = [
  { value: 'ophthalmologist', label: 'Ophtalmologiste', color: 'bg-blue-100 text-blue-800' },
  { value: 'intern', label: 'Interne', color: 'bg-green-100 text-green-800' },
  { value: 'tresorier', label: 'Trésorier', color: 'bg-amber-100 text-amber-800' },
  { value: 'auteur', label: 'Auteur', color: 'bg-indigo-100 text-indigo-800' },
  { value: 'regional', label: 'Resp. Région', color: 'bg-orange-100 text-orange-800' },
  { value: 'super_admin', label: 'Super Admin', color: 'bg-purple-100 text-purple-800' },
];

const getRoleBadge = (role) => {
  const r = ROLE_OPTIONS.find(o => o.value === role);
  if (!r) return <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-gray-100 text-gray-800">{role}</span>;
  return <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold ${r.color}`}>{r.label}</span>;
};

const getStatusBadge = (m) => {
  const sub = m.subscription_status || m.status;
  if (sub === 'pending') return <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-amber-100 text-amber-700"><span className="w-1.5 h-1.5 rounded-full bg-amber-500"/>En attente</span>;
  if (sub === 'active') return <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-emerald-100 text-emerald-700"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500"/>Actif</span>;
  if (sub === 'expired' || (m.subscription_expires && new Date(m.subscription_expires) < new Date())) return <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-red-100 text-red-700"><span className="w-1.5 h-1.5 rounded-full bg-red-500"/>Expiré</span>;
  return <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-gray-100 text-gray-600">Inactif</span>;
};

const formatDate = (d) => d ? new Intl.DateTimeFormat('fr-FR', { day:'numeric', month:'short', year:'numeric' }).format(new Date(d)) : '--';

// ===== MEMBER DETAIL VIEW =====
const MemberDetailView = ({ member, onBack, onUpdate, currentUser }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    first_name: member.first_name || '',
    last_name: member.last_name || '',
    phone: member.phone || '',
    region: member.region || '',
    address: member.address || '',
    city: member.city || '',
    postal_code: member.postal_code || '',
    specialties: Array.isArray(member.specialties) ? member.specialties.join(', ') : (member.specialty || ''),
    consultation_phone: member.consultation_phone || '',
    consultation_hours: member.consultation_hours || '',
    bio: member.bio || '',
    website: member.website || '',
    doctolib_url: member.doctolib_url || '',
    visible_in_directory: member.visible_in_directory !== false,
    profile_image: member.profile_image || '',
  });
  const token = localStorage.getItem('token');

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.specialties) payload.specialties = payload.specialties.split(',').map(s => s.trim()).filter(Boolean);
      else payload.specialties = [];
      const res = await fetch(`${API}/api/users/${member.id}/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        const updated = await res.json();
        toast.success('Profil mis a jour');
        setEditing(false);
        onUpdate(updated);
      } else toast.error('Erreur de sauvegarde');
    } catch { toast.error('Erreur de connexion'); }
    setSaving(false);
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API}/api/upload/avatar`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd
      });
      if (res.ok) {
        const { url } = await res.json();
        setForm(p => ({ ...p, profile_image: url }));
        // Save immediately
        await fetch(`${API}/api/users/${member.id}/profile`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ profile_image: url })
        });
        toast.success('Avatar mis a jour');
        onUpdate({ ...member, profile_image: url });
      }
    } catch { toast.error('Erreur upload'); }
    setUploading(false);
  };

  const handleRolesChange = async (newRoles, managedRegion) => {
    if (!newRoles || newRoles.length === 0) return;
    try {
      const body = { roles: newRoles };
      if (managedRegion !== undefined) body.managed_region = managedRegion;
      const res = await fetch(`${API}/api/users/${member.id}/role`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      if (res.ok) {
        toast.success('Rôles mis à jour');
        onUpdate({ ...member, role: newRoles[0], roles: newRoles, ...(managedRegion ? { managed_region: managedRegion } : {}) });
      } else toast.error('Erreur');
    } catch { toast.error('Erreur'); }
  };

  // ----- Regions list (for regional admin assignment) -----
  const [regionsList, setRegionsList] = useState([]);
  const [showRegionPicker, setShowRegionPicker] = useState(false);
  useEffect(() => {
    const loadRegions = async () => {
      try {
        const res = await fetch(`${API}/api/regions`);
        if (res.ok) setRegionsList(await res.json());
      } catch { /* silent */ }
    };
    loadRegions();
  }, []);

  // ----- Fiches annuaire (multiples) -----
  const [fiches, setFiches] = useState([]);
  const [fichesLoading, setFichesLoading] = useState(false);

  useEffect(() => {
    const loadFiches = async () => {
      setFichesLoading(true);
      try {
        const res = await fetch(`${API}/api/admin/users/${member.id}/fiches`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) setFiches(await res.json());
      } catch { /* silent */ }
      setFichesLoading(false);
    };
    loadFiches();
    // eslint-disable-next-line
  }, [member.id]);

  const toggleFichePublish = async (fiche) => {
    const newPublished = !fiche.published;
    try {
      const res = await fetch(`${API}/api/admin/users/${member.id}/fiches/${fiche.id}/publish`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ published: newPublished })
      });
      if (res.ok) {
        setFiches(prev => prev.map(f => f.id === fiche.id ? { ...f, published: newPublished } : f));
        toast.success(newPublished ? 'Fiche publiée' : 'Fiche masquée');
      } else toast.error('Erreur');
    } catch { toast.error('Erreur'); }
  };

  const handleApprove = async () => {
    try {
      const res = await fetch(`${API}/api/users/${member.id}/approve`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { toast.success('Membre approuve'); onUpdate({ ...member, status: 'active' }); }
    } catch { toast.error('Erreur'); }
  };

  const handleReject = async () => {
    try {
      const res = await fetch(`${API}/api/users/${member.id}/reject`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { toast.success('Membre refuse'); onUpdate({ ...member, status: 'rejected' }); }
    } catch { toast.error('Erreur'); }
  };

  const avatarUrl = form.profile_image ? `${API}${form.profile_image}` : null;

  return (
    <div className="space-y-6" data-testid="member-detail-view">
      <div className="flex items-center gap-4">
        <button onClick={onBack} className="w-10 h-10 rounded-xl bg-white border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors shadow-sm" data-testid="member-back-btn">
          <ArrowLeft size={18} className="text-gray-600" />
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-gray-900 tracking-tight">{member.first_name} {member.last_name}</h1>
          <div className="flex items-center gap-2 mt-1">
            {getRoleBadge(member.role)}
            {getStatusBadge(member)}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {member.status === 'pending' && (
            <>
              <button onClick={handleApprove} className="px-4 py-2 bg-emerald-600 text-white rounded-xl text-sm font-semibold hover:bg-emerald-700 transition-colors" data-testid="approve-member"><CheckCircle size={16} className="inline mr-1"/>Approuver</button>
              <button onClick={handleReject} className="px-4 py-2 bg-red-500 text-white rounded-xl text-sm font-semibold hover:bg-red-600 transition-colors" data-testid="reject-member"><XCircle size={16} className="inline mr-1"/>Refuser</button>
            </>
          )}
          {!editing ? (
            <button onClick={() => setEditing(true)} className="px-4 py-2 bg-[#1e3a5f] text-white rounded-xl text-sm font-semibold hover:bg-[#2a4a6f] transition-colors shadow-sm" data-testid="edit-member-btn">
              <Edit size={16} className="inline mr-1"/> Modifier
            </button>
          ) : (
            <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-emerald-600 text-white rounded-xl text-sm font-semibold hover:bg-emerald-700 transition-colors shadow-sm" data-testid="save-member-btn">
              {saving ? <Loader2 size={16} className="inline mr-1 animate-spin"/> : <Save size={16} className="inline mr-1"/>} Enregistrer
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LEFT COLUMN — Avatar, Roles, Adhésion, Carte étudiant */}
        <div className="space-y-6">
          {/* Avatar / contact card */}
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
            <div className="bg-gradient-to-r from-[#1e3a5f] to-[#35AFE4] p-6 flex flex-col items-center">
              <div className="relative group">
                <div className="w-24 h-24 rounded-full bg-white/20 backdrop-blur-sm border-4 border-white/40 overflow-hidden flex items-center justify-center text-white font-bold text-3xl shadow-xl">
                  {avatarUrl ? <img src={avatarUrl} alt="" className="w-full h-full object-cover" /> : `${member.first_name?.[0] || ''}${member.last_name?.[0] || ''}`}
                </div>
                <label className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity">
                  {uploading ? <Loader2 size={20} className="text-white animate-spin" /> : <Upload size={20} className="text-white" />}
                  <input type="file" accept="image/*" onChange={handleAvatarUpload} className="hidden" data-testid="avatar-upload" />
                </label>
              </div>
              <h2 className="text-white font-bold text-lg mt-3">{member.first_name} {member.last_name}</h2>
              <p className="text-white/60 text-sm">{member.email}</p>
            </div>
            <div className="p-5 space-y-3">
              {member.phone && <div className="flex items-center gap-3 text-sm"><Phone size={14} className="text-gray-400"/><span className="text-gray-700">{member.phone}</span></div>}
              {member.region && <div className="flex items-center gap-3 text-sm"><MapPin size={14} className="text-gray-400"/><span className="text-gray-700">{member.region}</span></div>}
              {member.city && <div className="flex items-center gap-3 text-sm"><MapPin size={14} className="text-gray-400"/><span className="text-gray-700">{[member.address, member.postal_code, member.city].filter(Boolean).join(', ')}</span></div>}
              {member.website && <div className="flex items-center gap-3 text-sm"><Globe size={14} className="text-gray-400"/><a href={member.website} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline truncate">{member.website}</a></div>}
            </div>
          </div>

          {/* Roles (multi) */}
          <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
            <h3 className="text-xs uppercase tracking-wider text-gray-400 font-bold mb-3 flex items-center gap-2"><Shield size={14}/>Rôles ({((member.roles && member.roles.length) ? member.roles.length : 1)})</h3>
            <p className="text-[11px] text-gray-400 mb-3">Un membre peut avoir plusieurs rôles. Le 1er sélectionné est le rôle principal.</p>
            <div className="flex flex-wrap gap-2">
              {ROLE_OPTIONS.map(opt => {
                const memberRoles = (member.roles && member.roles.length) ? member.roles : [member.role];
                const active = memberRoles.includes(opt.value);
                return (
                  <button
                    key={opt.value}
                    onClick={() => {
                      const next = active ? memberRoles.filter(r => r !== opt.value) : [...memberRoles, opt.value];
                      if (next.length === 0) { toast.error('Au moins un rôle est requis'); return; }
                      // If turning ON "regional" without an assigned region → open the picker
                      if (!active && opt.value === 'regional' && !member.managed_region) {
                        setShowRegionPicker(true);
                        return;
                      }
                      handleRolesChange(next);
                    }}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
                      active ? `${opt.color} ring-1 ring-current/20 shadow-sm` : 'bg-gray-50 text-gray-400 hover:bg-gray-100 hover:text-gray-600'
                    }`}
                    data-testid={`role-chip-${opt.value}`}>
                    {active && <CheckCircle size={11} />}
                    {opt.label}
                  </button>
                );
              })}
            </div>

            {/* Managed region (visible if regional role active) */}
            {(((member.roles && member.roles.length) ? member.roles : [member.role]).includes('regional') || showRegionPicker) && (
              <div className="mt-4 p-3 rounded-xl bg-orange-50/60 border border-orange-100" data-testid="region-picker">
                <label className="text-[11px] font-bold text-orange-700 uppercase tracking-wider block mb-2">Région gérée *</label>
                <select
                  value={member.managed_region || ''}
                  onChange={async (e) => {
                    const region = e.target.value;
                    if (!region) return;
                    const memberRoles = (member.roles && member.roles.length) ? member.roles : [member.role];
                    const next = memberRoles.includes('regional') ? memberRoles : [...memberRoles, 'regional'];
                    await handleRolesChange(next, region);
                    setShowRegionPicker(false);
                  }}
                  className="w-full px-3 py-2 rounded-lg border border-orange-200 bg-white text-sm font-medium"
                  data-testid="managed-region-select">
                  <option value="">— Choisir une région —</option>
                  {regionsList.map(r => (
                    <option key={r.id || r.slug || r.name} value={r.name}>{r.name}</option>
                  ))}
                </select>
                {!member.managed_region && (
                  <p className="text-[11px] text-orange-700 mt-2">Le rôle "Resp. Région" ne sera activé qu'après sélection d'une région.</p>
                )}
              </div>
            )}

            {/* Receipt-generation permission toggle (super_admin only, on regional members) */}
            {currentUser?.role === 'super_admin' && (((member.roles && member.roles.length) ? member.roles : [member.role]).includes('regional')) && (
              <div className="mt-4 p-3 rounded-xl bg-violet-50/60 border border-violet-100" data-testid="receipt-permission-toggle">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-violet-900 flex items-center gap-1.5"><FileText size={13}/>Reçus fiscaux</p>
                    <p className="text-[11px] text-violet-700 mt-0.5">
                      {member.can_generate_receipts
                        ? "Ce responsable régional peut créer/envoyer des reçus fiscaux."
                        : "Ce responsable régional peut uniquement consulter les reçus fiscaux existants."}
                    </p>
                  </div>
                  <button
                    onClick={async () => {
                      try {
                        const res = await fetch(`${API}/api/users/${member.id}/permissions`, {
                          method: 'PUT',
                          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                          body: JSON.stringify({ can_generate_receipts: !member.can_generate_receipts })
                        });
                        if (res.ok) {
                          const updated = await res.json();
                          toast.success(updated.can_generate_receipts ? 'Génération de reçus activée' : 'Génération de reçus désactivée');
                          onUpdate(updated);
                        } else { toast.error('Erreur'); }
                      } catch { toast.error('Erreur de connexion'); }
                    }}
                    className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${member.can_generate_receipts ? 'bg-violet-500' : 'bg-gray-300'}`}
                    title={member.can_generate_receipts ? 'Cliquez pour désactiver' : 'Cliquez pour activer'}
                    data-testid="toggle-receipt-permission">
                    <span className={`absolute top-[2px] bg-white rounded-full h-5 w-5 transition-all shadow-sm ${member.can_generate_receipts ? 'left-[22px]' : 'left-[2px]'}`} />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Adhesion */}
          <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
            <h3 className="text-xs uppercase tracking-wider text-gray-400 font-bold mb-3 flex items-center gap-2"><CreditCard size={14}/>Adhésion</h3>
            <div className="space-y-2">
              <div className="flex justify-between text-sm"><span className="text-gray-500">Montant</span><span className="font-bold text-gray-900">{member.last_payment_amount ? `${member.last_payment_amount}€` : member.subscription_amount ? `${member.subscription_amount}€` : '--'}</span></div>
              <div className="flex justify-between text-sm"><span className="text-gray-500">Expiration</span><span className="font-medium text-gray-900">{formatDate(member.subscription_expires)}</span></div>
              <div className="flex justify-between text-sm"><span className="text-gray-500">Paiement</span><span className="font-medium text-gray-900">{member.payment_method || '--'}</span></div>
              <div className="flex justify-between text-sm"><span className="text-gray-500">Inscrit le</span><span className="font-medium text-gray-900">{formatDate(member.created_at)}</span></div>
            </div>
          </div>

          {member.role === 'intern' && (
            <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm" data-testid="admin-intern-card-block">
              <h3 className="text-xs uppercase tracking-wider text-gray-400 font-bold mb-3 flex items-center gap-2"><FileText size={14}/>Carte étudiante</h3>
              <div className="space-y-2 text-sm mb-3">
                <div className="flex justify-between"><span className="text-gray-500">Année</span><span className="font-semibold text-gray-900">{member.student_year || '—'}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Expire le</span><span className="font-semibold text-gray-900">{formatDate(member.student_card_expiry)}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Statut</span>
                  <span className={`font-bold ${
                    member.student_card_status === 'approved' ? 'text-emerald-700' :
                    member.student_card_status === 'rejected' ? 'text-red-700' :
                    member.student_card_status === 'expired' ? 'text-red-700' : 'text-amber-700'
                  }`}>{member.student_card_status || 'pending'}</span></div>
              </div>
              {member.student_card_url && (
                <img src={`${API}${member.student_card_url}`} alt="Carte"
                  className="w-full rounded-lg border mb-3" data-testid="admin-card-image" />
              )}
              <div className="flex gap-2">
                <button
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('token');
                      const res = await fetch(`${API}/api/admin/users/${member.id}/student-card/validate`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                        body: JSON.stringify({ action: 'approve' }),
                      });
                      if (!res.ok) throw new Error('Erreur');
                      toast.success('Carte validée');
                      onUpdate?.();
                    } catch { toast.error('Erreur'); }
                  }}
                  className="flex-1 px-3 py-2 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 text-xs font-bold transition-colors"
                  data-testid="admin-approve-card-btn">
                  Valider
                </button>
                <button
                  onClick={async () => {
                    const reason = window.prompt('Motif du refus (optionnel) :') || '';
                    try {
                      const token = localStorage.getItem('token');
                      const res = await fetch(`${API}/api/admin/users/${member.id}/student-card/validate`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                        body: JSON.stringify({ action: 'reject', reason }),
                      });
                      if (!res.ok) throw new Error('Erreur');
                      toast.success('Carte refusée');
                      onUpdate?.();
                    } catch { toast.error('Erreur'); }
                  }}
                  className="flex-1 px-3 py-2 rounded-lg bg-red-50 text-red-700 hover:bg-red-100 text-xs font-bold transition-colors"
                  data-testid="admin-reject-card-btn">
                  Refuser
                </button>
              </div>
            </div>
          )}
        </div>

        {/* RIGHT COLUMN — Identity & Fiches */}
        <div className="lg:col-span-2 space-y-6">
          {/* Identity & Contact */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xs uppercase tracking-wider text-gray-400 font-bold flex items-center gap-2"><IdCard size={14}/>Identité & Contact</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block mb-1">Prénom</label>
                <input value={form.first_name} onChange={e => setForm(p => ({...p, first_name: e.target.value}))} disabled={!editing}
                  className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm disabled:bg-gray-50 disabled:text-gray-600" data-testid="edit-first-name" />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block mb-1">Nom</label>
                <input value={form.last_name} onChange={e => setForm(p => ({...p, last_name: e.target.value}))} disabled={!editing}
                  className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm disabled:bg-gray-50 disabled:text-gray-600" data-testid="edit-last-name" />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block mb-1">Téléphone</label>
                <input value={form.phone} onChange={e => setForm(p => ({...p, phone: e.target.value}))} disabled={!editing}
                  className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm disabled:bg-gray-50 disabled:text-gray-600" />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block mb-1">Région</label>
                <input value={form.region} onChange={e => setForm(p => ({...p, region: e.target.value}))} disabled={!editing}
                  className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm disabled:bg-gray-50 disabled:text-gray-600" />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block mb-1">Adresse</label>
                <input value={form.address} onChange={e => setForm(p => ({...p, address: e.target.value}))} disabled={!editing}
                  className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm disabled:bg-gray-50 disabled:text-gray-600" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block mb-1">CP</label>
                  <input value={form.postal_code} onChange={e => setForm(p => ({...p, postal_code: e.target.value}))} disabled={!editing}
                    className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm disabled:bg-gray-50 disabled:text-gray-600" />
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block mb-1">Ville</label>
                  <input value={form.city} onChange={e => setForm(p => ({...p, city: e.target.value}))} disabled={!editing}
                    className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm disabled:bg-gray-50 disabled:text-gray-600" />
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block mb-1">Biographie</label>
                <textarea value={form.bio} onChange={e => setForm(p => ({...p, bio: e.target.value}))} disabled={!editing} rows={3}
                  className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm disabled:bg-gray-50 disabled:text-gray-600 resize-none" />
              </div>
            </div>
          </div>

          {/* Fiches Annuaire (multiples) */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm" data-testid="member-fiches-section">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-xs uppercase tracking-wider text-gray-400 font-bold flex items-center gap-2"><Stethoscope size={14}/>Fiches annuaire ({fiches.length})</h3>
                <p className="text-[11px] text-gray-400 mt-1">Le membre peut avoir plusieurs cabinets. Cochez celles à rendre publiques.</p>
              </div>
              <a href={`/admin/directory?user=${member.id}`} className="text-xs font-semibold text-[#35AFE4] hover:text-[#1e3a5f] flex items-center gap-1" data-testid="open-fiches-admin">
                Gérer toutes ses fiches <ExternalLink size={11} />
              </a>
            </div>
            {fichesLoading ? (
              <div className="flex items-center justify-center py-8"><Loader2 className="animate-spin text-gray-300" size={20} /></div>
            ) : fiches.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-xl">
                <Stethoscope size={28} className="mx-auto text-gray-300 mb-2" />
                <p className="text-sm text-gray-500 font-medium">Aucune fiche annuaire</p>
                <p className="text-xs text-gray-400">Ce membre n'a pas encore créé de fiche cabinet.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {fiches.map(fiche => (
                  <div key={fiche.id} className="flex items-center gap-3 bg-gray-50/60 hover:bg-gray-50 rounded-xl p-3 transition-colors group" data-testid={`fiche-row-${fiche.id}`}>
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${fiche.published ? 'bg-emerald-100 text-emerald-600' : 'bg-gray-200 text-gray-400'}`}>
                      {fiche.published ? <Eye size={15} /> : <EyeOff size={15} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{fiche.cabinet_name || fiche.name || `Cabinet ${fiche.city || ''}`.trim()}</p>
                      <p className="text-[11px] text-gray-500 flex items-center gap-1.5"><MapPin size={10} />{[fiche.address, fiche.postal_code, fiche.city].filter(Boolean).join(', ') || '—'}</p>
                    </div>
                    <button onClick={() => toggleFichePublish(fiche)}
                      className={`relative w-10 h-5 rounded-full transition-colors flex-shrink-0 ${fiche.published ? 'bg-emerald-500' : 'bg-gray-300'}`}
                      title={fiche.published ? 'Cliquez pour masquer' : 'Cliquez pour publier'}
                      data-testid={`toggle-fiche-${fiche.id}`}>
                      <span className={`absolute top-[2px] bg-white rounded-full h-4 w-4 transition-all ${fiche.published ? 'left-[22px]' : 'left-[2px]'}`} />
                    </button>
                    <a href={`/admin/directory?user=${member.id}&fiche=${fiche.id}`}
                      className="p-2 rounded-lg hover:bg-white text-gray-400 hover:text-[#35AFE4] transition-colors flex-shrink-0"
                      title="Modifier la fiche">
                      <Edit size={14} />
                    </a>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ===== MAIN PAGE =====
const MembersCentralPage = () => {
  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [selectedMember, setSelectedMember] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const token = localStorage.getItem('token');

  useEffect(() => { fetchMembers(); fetchMe(); }, []);
  useEffect(() => { applyFilters(); }, [members, searchTerm, activeTab, roleFilter]);

  const fetchMe = async () => {
    try {
      const res = await fetch(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setCurrentUser(await res.json());
    } catch { /* silent */ }
  };

  // Open the requested user when ?user=<id> is in the URL
  useEffect(() => {
    const uid = searchParams.get('user');
    if (uid && members.length > 0 && (!selectedMember || selectedMember.id !== uid)) {
      const target = members.find(m => m.id === uid);
      if (target) setSelectedMember(target);
    }
  }, [searchParams, members]);

  const fetchMembers = async () => {
    try {
      const res = await fetch(`${API}/api/users`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setMembers(await res.json());
    } catch { toast.error('Erreur de connexion'); }
    setLoading(false);
  };

  const applyFilters = () => {
    let f = [...members];
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      f = f.filter(m => `${m.first_name} ${m.last_name} ${m.email}`.toLowerCase().includes(s));
    }
    if (activeTab === 'pending') f = f.filter(m => (m.subscription_status || m.status) === 'pending');
    else if (activeTab === 'active') f = f.filter(m => (m.subscription_status || m.status) === 'active');
    else if (activeTab === 'expired') f = f.filter(m => m.subscription_expires && new Date(m.subscription_expires) < new Date());
    if (roleFilter) f = f.filter(m => m.role === roleFilter);
    f.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
    setFilteredMembers(f);
  };

  const handleApprove = async (e, userId) => {
    e.stopPropagation();
    try {
      const res = await fetch(`${API}/api/users/${userId}/approve`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { toast.success('Membre approuve'); fetchMembers(); }
    } catch { toast.error('Erreur'); }
  };

  // Count all members fetched (the API already scopes by role for regional admins).
  // Regional admins see themselves + bureau members + region users; super_admin sees everyone.
  const stats = {
    total: members.length,
    pending: members.filter(m => (m.subscription_status || m.status) === 'pending').length,
    active: members.filter(m => (m.subscription_status || m.status) === 'active').length,
    ophtalmos: members.filter(m => m.role === 'ophthalmologist').length,
    interns: members.filter(m => m.role === 'intern').length,
  };

  const handleMemberUpdate = (updated) => {
    setMembers(prev => prev.map(m => m.id === updated.id ? { ...m, ...updated } : m));
    setSelectedMember(prev => prev ? { ...prev, ...updated } : null);
  };

  if (selectedMember) {
    return (
      <AdminLayout>
        <div className="p-4 sm:p-6 max-w-[1400px] mx-auto">
          <MemberDetailView member={selectedMember} currentUser={currentUser} onBack={() => { setSelectedMember(null); setSearchParams({}); fetchMembers(); }} onUpdate={handleMemberUpdate} />
        </div>
      </AdminLayout>
    );
  }

  const tabs = [
    { id: 'all', label: 'Tous', count: stats.total },
    { id: 'active', label: 'Actifs', count: stats.active },
    { id: 'pending', label: 'En attente', count: stats.pending },
    { id: 'expired', label: 'Expires', count: members.filter(m => m.subscription_expires && new Date(m.subscription_expires) < new Date()).length },
  ];

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 max-w-[1600px] mx-auto space-y-5" data-testid="members-central-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-violet-100 flex items-center justify-center shadow-sm"><Users className="text-violet-600" size={18}/></div>
            <div>
              <h1 className="text-xl font-bold text-gray-900 tracking-tight">Membres & Adhesions</h1>
              <p className="text-xs text-gray-400 mt-0.5">{stats.total} membres inscrits</p>
            </div>
          </div>
          <Button
            variant="outline"
            className="flex items-center gap-2 text-sm"
            data-testid="export-csv-btn"
            onClick={() => {
              fetch(`${API}/api/users/export-csv`, { headers: { Authorization: `Bearer ${token}` } })
                .then(r => r.blob())
                .then(blob => {
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `membres_sfoalc_${new Date().toISOString().slice(0,10)}.csv`;
                  document.body.appendChild(a); a.click(); document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                  toast.success('Export CSV téléchargé');
                })
                .catch(() => toast.error("Erreur lors de l'export"));
            }}
          >
            <Download size={14} /> Exporter CSV
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
            <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center mb-2"><Users size={14}/></div>
            <p className="text-xl font-bold text-gray-900">{stats.total}</p>
            <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Total</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
            <div className="w-8 h-8 bg-emerald-100 text-emerald-600 rounded-lg flex items-center justify-center mb-2"><CheckCircle size={14}/></div>
            <p className="text-xl font-bold text-emerald-600">{stats.active}</p>
            <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Actifs</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
            <div className="w-8 h-8 bg-amber-100 text-amber-600 rounded-lg flex items-center justify-center mb-2"><Clock size={14}/></div>
            <p className="text-xl font-bold text-amber-600">{stats.pending}</p>
            <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">En attente</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
            <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center mb-2"><Stethoscope size={14}/></div>
            <p className="text-xl font-bold text-gray-900">{stats.ophtalmos}</p>
            <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Ophtalmos</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
            <div className="w-8 h-8 bg-green-100 text-green-600 rounded-lg flex items-center justify-center mb-2"><UserCheck size={14}/></div>
            <p className="text-xl font-bold text-gray-900">{stats.interns}</p>
            <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Internes</p>
          </div>
        </div>

        {/* Members List with AdminListView */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm" data-testid="members-table" style={{ height: 'calc(100vh - 380px)', minHeight: '400px' }}>
          <AdminListView
            data={filteredMembers}
            loading={loading}
            idKey="id"
            pageSize={25}
            searchValue={searchTerm}
            onSearchChange={setSearchTerm}
            searchPlaceholder="Rechercher un membre..."
            emptyMessage="Aucun membre trouvé"
            filters={[
              {
                key: 'role',
                label: 'Tous les rôles',
                value: roleFilter,
                onChange: setRoleFilter,
                options: [
                  { value: 'ophthalmologist', label: 'Ophtalmologiste' },
                  { value: 'intern', label: 'Interne' },
                  { value: 'auteur', label: 'Auteur' },
                  { value: 'regional', label: 'Resp. Région' },
                  { value: 'super_admin', label: 'Super Admin' },
                ],
              },
              {
                key: 'status',
                label: 'Tous les statuts',
                value: activeTab === 'all' ? '' : activeTab,
                onChange: (v) => setActiveTab(v || 'all'),
                options: [
                  { value: 'active', label: 'Actifs' },
                  { value: 'pending', label: 'En attente' },
                  { value: 'expired', label: 'Expirés' },
                ],
              },
            ]}
            columns={[
              {
                key: 'first_name', label: 'Membre', sortable: true,
                render: (_, m) => (
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#1e3a5f] to-[#35AFE4] flex items-center justify-center text-white font-bold text-[10px] flex-shrink-0 overflow-hidden">
                      {m.profile_image ? <img src={m.profile_image.startsWith('http') ? m.profile_image : `${API}${m.profile_image}`} alt="" className="w-full h-full object-cover"/> : `${m.first_name?.[0] || ''}${m.last_name?.[0] || ''}`}
                    </div>
                    <span className="text-sm font-semibold text-gray-900">{m.first_name} {m.last_name}</span>
                  </div>
                ),
              },
              { key: 'email', label: 'Email', sortable: true },
              {
                key: 'role', label: 'Rôle', sortable: true,
                render: (role) => getRoleBadge(role),
              },
              {
                key: 'subscription_status', label: 'Statut', sortable: true,
                render: (_, m) => getStatusBadge(m),
              },
              { key: 'region', label: 'Région', sortable: true },
              {
                key: 'last_payment_amount', label: 'Adhésion', sortable: true,
                render: (_, m) => (
                  <span className="font-bold text-gray-900">{m.last_payment_amount ? `${m.last_payment_amount}€` : m.subscription_amount ? `${m.subscription_amount}€` : '--'}</span>
                ),
              },
            ]}
            onRowClick={(m) => setSelectedMember(m)}
            bulkActions={[
              {
                label: 'Exporter CSV',
                icon: Download,
                onClick: async (ids) => {
                  try {
                    const res = await fetch(`${API}/admin/bulk/members/export`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ ids, fields: ['first_name', 'last_name', 'email', 'role', 'region', 'subscription_status', 'phone', 'city'] }),
                    });
                    if (res.ok) {
                      const blob = await res.blob();
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url; a.download = `membres_${new Date().toISOString().slice(0,10)}.csv`;
                      a.click(); URL.revokeObjectURL(url);
                      toast.success(`${ids.length} membre(s) exporté(s)`);
                    } else {
                      toast.error("Erreur d'export : accès refusé");
                    }
                  } catch { toast.error("Erreur d'export"); }
                },
              },
              {
                label: 'Supprimer',
                icon: Trash2,
                variant: 'danger',
                onClick: async (ids) => {
                  const myId = currentUser?.id;
                  const safeIds = ids.filter(id => id !== myId);
                  if (safeIds.length === 0) { toast.error('Vous ne pouvez pas supprimer votre propre compte'); return; }
                  if (!window.confirm(`Supprimer ${safeIds.length} membre(s) ?`)) return;
                  try {
                    const res = await fetch(`${API}/admin/bulk/members/delete`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ ids: safeIds }),
                    });
                    if (res.ok) {
                      const data = await res.json();
                      toast.success(`${data.deleted} membre(s) supprimé(s)`);
                      fetchMembers();
                    } else {
                      toast.error('Erreur de suppression : accès refusé');
                    }
                  } catch { toast.error('Erreur'); }
                },
              },
            ]}
            headerActions={
              <Button variant="outline" className="flex items-center gap-2 text-xs" data-testid="export-all-csv-btn"
                onClick={() => {
                  fetch(`${API}/api/users/export-csv`, { headers: { Authorization: `Bearer ${token}` } })
                    .then(r => r.blob())
                    .then(blob => {
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url; a.download = `membres_sfoalc_${new Date().toISOString().slice(0,10)}.csv`;
                      document.body.appendChild(a); a.click(); document.body.removeChild(a);
                      URL.revokeObjectURL(url);
                      toast.success('Export CSV téléchargé');
                    })
                    .catch(() => toast.error("Erreur d'export"));
                }}>
                <Download size={13} /> Exporter tout
              </Button>
            }
          />
        </div>
      </div>
    </AdminLayout>
  );
};

export default MembersCentralPage;
